"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  GenerateMarketplaceFiltersResponseDto,
  GeoFilter,
  MarketplaceFilterDto,
  ResolvedMapLocation,
} from "@workspace/dto/job/generate-marketplace-filters";
import { Button } from "@workspace/ui/components/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@workspace/ui/components/input-group";
import { cn } from "@workspace/ui/lib/utils";
import { Loader2, Sparkles } from "lucide-react";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { axios } from "@/lib/axios";
import { getMutationNetworkErrorToast } from "@/lib/query-failure";
import { queryKeys } from "@/lib/query-keys";

type GenerateFiltersRequest = {
  prompt: string;
  context?: string;
  userLatitude?: number;
  userLongitude?: number;
};

export type AiFilterResult = {
  filters: Record<string, unknown>;
  location: {
    location_query: string | null;
    near_me: boolean;
    radius_km: number | null;
  } | null;
  map: ResolvedMapLocation | null;
  geoFilter: GeoFilter | null;
};

type MarketplaceAiFilterInputProps = {
  onFiltersGenerated: (result: AiFilterResult) => void;
  className?: string;
  userLatitude?: number;
  userLongitude?: number;
};

const PLACEHOLDERS = [
  "Remote software development jobs",
  "Part-time teaching positions",
  "High priority healthcare roles",
  "Full-time engineering jobs in Cape Town",
  "Hourly customer service roles",
  "Marketing jobs with flexible hours",
];

// Helper function to check if an array has valid values
function hasValidArrayValues(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

function transformResult(
  response: GenerateMarketplaceFiltersResponseDto
): AiFilterResult {
  const transformedFilters: Record<string, unknown> = {};

  // Array fields that should only be included if they have values
  const arrayFields = [
    "compensation",
    "compensation_rate",
    "category",
    "work_model",
    "commitment",
    "priority",
    "resume",
  ];

  arrayFields.forEach((field) => {
    const fieldValue = response.filters[field as keyof MarketplaceFilterDto];
    if (fieldValue && hasValidArrayValues(fieldValue)) {
      transformedFilters[field] = fieldValue;
    }
  });

  // Special handling for posted_date (requires exactly 2 dates)
  if (
    response.filters.posted_date &&
    Array.isArray(response.filters.posted_date) &&
    response.filters.posted_date.length === 2
  ) {
    const startDate = new Date(response.filters.posted_date[0] as string);
    const endDate = new Date(response.filters.posted_date[1] as string);
    transformedFilters.posted_date = [startDate, endDate];
  }

  return {
    filters: transformedFilters,
    location: response.location ?? null,
    map: response.map ?? null,
    geoFilter: response.geoFilter ?? null,
  };
}

export function MarketplaceAiFilterInput({
  onFiltersGenerated,
  className,
  userLatitude,
  userLongitude,
}: MarketplaceAiFilterInputProps) {
  const [prompt, setPrompt] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const queryClient = useQueryClient();

  const generateFiltersMutation = useMutation<
    GenerateMarketplaceFiltersResponseDto,
    Error,
    GenerateFiltersRequest
  >({
    mutationFn: async ({
      prompt: filterPrompt,
      context,
    }: GenerateFiltersRequest) => {
      const { data: responseData } =
        await axios.post<GenerateMarketplaceFiltersResponseDto>(
          "/jobs/generate-marketplace-filters",
          {
            prompt: filterPrompt,
            context,
            ...(userLatitude != null &&
              userLongitude != null && {
                userLatitude,
                userLongitude,
              }),
          }
        );
      return responseData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.marketplace.nearby(),
      });
    },
    onError: () => {
      // Error handling is done via the mutation state
    },
  });

  // Set random placeholder on mount
  useEffect(() => {
    const randomPlaceholder =
      PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)] ??
      "Describe the jobs you're looking for...";
    // react-doctor-disable-next-line react-doctor/no-initialize-state -- random placeholder is set on the client after mount to avoid an SSR/client hydration mismatch
    setPlaceholder(randomPlaceholder);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!prompt.trim() || prompt.split(" ").length < 2) {
      return;
    }

    try {
      const result = await generateFiltersMutation.mutateAsync({
        prompt: prompt.trim(),
        context: `Available job categories include technology, healthcare, education, finance, and many more. 
                  Work models can be onsite, hybrid, or remote. 
                  Commitment levels include part-time, full-time, once-off, and on-demand.`,
      });

      const transformedResult = transformResult(result);
      onFiltersGenerated(transformedResult);

      // Clear the prompt after successful generation
      setPrompt("");
    } catch {
      // Error handling is done via the mutation state
    }
  };

  const isLoading = generateFiltersMutation.isPending;
  const filterErrorMessage = (() => {
    const error = generateFiltersMutation.error;
    if (!error) {
      return null;
    }

    const networkToast = getMutationNetworkErrorToast(
      error,
      "Couldn't generate filters"
    );
    if (networkToast) {
      return networkToast.description;
    }

    return "Failed to generate filters. Please try again.";
  })();

  return (
    <div className={cn("space-y-3", className)}>
      <form className="space-y-3" onSubmit={handleSubmit}>
        <InputGroup>
          <InputGroupInput
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            className="h-9 rounded-lg"
            disabled={isLoading}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={placeholder}
            spellCheck="false"
            value={prompt}
          />
          <InputGroupAddon>
            <Sparkles className="size-4 text-primary" />
          </InputGroupAddon>
        </InputGroup>
        <Button
          className="w-full"
          disabled={!prompt.trim() || prompt.split(" ").length < 2 || isLoading}
          size="sm"
          type="submit"
        >
          {isLoading ? <Loader2 className="size-3 animate-spin" /> : "Generate"}
        </Button>
      </form>

      {filterErrorMessage ? (
        <p className="text-destructive text-sm">{filterErrorMessage}</p>
      ) : null}
    </div>
  );
}
